import React from "react";
import { Box, List } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from 'prop-types';
import { useNavigation } from "../../context/NavigationContext";
import { getNavigationMenuItems, getLogo } from "../../utils/navigationUtils";
import NavigationMenuItem from "./NavigationMenuItem";
import ROUTES from "../../config/routes";

/**
 * @typedef {object} NavigationMenuProps
 * @property {object} [pageSectionRefsContainer] - Container with refs for intersection observer to track active sections.
 * @property {string} [textColor] - The color for the navigation text. Defaults to '#000000'.
 * @property {string} [primaryColor] - The primary color for active states and theming.
 * @property {number} [width] - The width of the navigation menu. Defaults to 250.
 * @property {boolean} [isHomePage] - Whether this is the home page (affects styling).
 */

/**
 * NavigationMenu component for desktop view.
 * Enhanced with better animations, glass morphism effects, and improved section detection.
 *
 * @param {NavigationMenuProps} props - The props for the component.
 * @returns {JSX.Element} The rendered NavigationMenu component.
 */
const NavigationMenu = ({ 
    pageSectionRefsContainer, 
    textColor = '#000000',
    primaryColor,
    width = 250,
    isHomePage = false 
}) => {
    const {
        expandedMenu,
        handleMenuToggle,
        activeSection
    } = useNavigation();
    const navigate = useNavigate();
    const location = useLocation();

    const menuItems = getNavigationMenuItems();
    const activeColor = primaryColor || '#6511af'; // Use dynamic color or fallback
    const currentTextColor = textColor;

    const logoSrc = getLogo(currentTextColor);
    const actualIsHomePage = location.pathname === ROUTES.HOME;

    return (
        <Box
            component="nav"
            sx={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                height: '100vh',
                width: width,
                background: actualIsHomePage 
                    ? 'transparent' // Transparent background for home page
                    : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: actualIsHomePage ? 'blur(10px)' : 'blur(10px)',
                WebkitBackdropFilter: actualIsHomePage ? 'blur(10px)' : 'blur(10px)', // Safari support
                borderLeft: actualIsHomePage 
                    ? `1px solid rgba(255, 255, 255, 0.2)` // Subtle border for home page
                    : `1px solid rgba(0, 0, 0, 0.1)`,
                color: currentTextColor,
                display: { xs: "none", md: "flex" },
                flexDirection: 'column',
                justifyContent: 'flex-start', // Changed from space-between to flex-start
                paddingTop: 4,
                paddingBottom: 4,
                zIndex: 1300,
                overflowY: 'auto',
                overflowX: 'hidden',
                boxSizing: 'border-box',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                    background: actualIsHomePage 
                        ? 'rgba(0, 0, 0, 0.05)' // Very subtle dark overlay on hover for home page
                        : 'rgba(255, 255, 255, 0.98)',
                }
            }}
        >
            {/* Navigation Items */}
            <Box sx={{ paddingTop: 8 }}>
                <List sx={{ padding: 0 }}>
                    {menuItems.map((item) => (
                        <NavigationMenuItem
                            key={item.title}
                            item={item}
                            expandedMenu={expandedMenu}
                            handleMenuToggle={handleMenuToggle}
                            location={location}
                            activeSection={activeSection}
                            textColor={currentTextColor}
                            activeColor={activeColor}
                            navigate={navigate}
                            pageSectionRefsContainer={pageSectionRefsContainer}
                        />
                    ))}
                </List>
            </Box>

            {/* Spacer between navigation and logo */}
            <Box sx={{ marginTop: 6 }} />

            {/* Logo positioned closer to navigation items */}
            {logoSrc && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingX: 2,
                        paddingBottom: 2
                    }}
                >
                    <Box
                        component="img"
                        src={logoSrc}
                        alt="Spinnaker Algo"
                        sx={{
                            maxWidth: '80%',
                            height: 'auto',
                            maxHeight: 60,
                            filter: actualIsHomePage ? 'brightness(1.2)' : 'none',
                            opacity: 0.8,
                            transition: 'opacity 0.3s ease',
                            '&:hover': {
                                opacity: 1
                            }
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

NavigationMenu.propTypes = {
    pageSectionRefsContainer: PropTypes.object,
    textColor: PropTypes.string,
    primaryColor: PropTypes.string,
    width: PropTypes.number,
    isHomePage: PropTypes.bool
};

export default NavigationMenu;